import { Component } from '@theme/component';

class NaptonBoatPage extends Component {
  currentPhoto = 0;
  mode = 'gallery';

  connectedCallback() {
    super.connectedCallback();
    this.abort?.abort();
    this.abort = new AbortController();
    this.photos = [...this.querySelectorAll('[data-photo]')];
    this.viewer = this.querySelector('.nbp-viewer');
    this.addEventListener('click', this.handleClick, { signal: this.abort.signal });
    this.addEventListener('keydown', this.handleKeydown, { signal: this.abort.signal });
    this.viewer?.addEventListener('close', () => this.viewerTrigger?.focus({ preventScroll: true }), { signal: this.abort.signal });
    this.observeEnquiry();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.enquiryObserver?.disconnect();
    if (this.viewer?.open) this.viewer.close();
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.photos = [...this.querySelectorAll('[data-photo]')];
    this.viewer = this.querySelector('.nbp-viewer');
    this.observeEnquiry();
  }

  observeEnquiry() {
    this.enquiryObserver?.disconnect();
    const enquiry = this.querySelector('.nbp-enquiry');
    if (!enquiry) return;
    this.enquiryObserver = new IntersectionObserver(entries => {
      this.toggleAttribute('data-enquiry-visible', entries[0].isIntersecting);
    }, { threshold: 0.1 });
    this.enquiryObserver.observe(enquiry);
  }

  handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const roomLink = event.target.closest('[data-explore-room]');
    if (roomLink && this.contains(roomLink)) {
      const explorer = this.querySelector('napton-plan-explorer');
      if (typeof explorer?.selectRoom !== 'function') return;
      event.preventDefault();
      explorer.selectRoom(roomLink.dataset.exploreRoom, true);
      const panel = [...explorer.querySelectorAll('[data-room-panel]')].find(item => item.dataset.roomPanel === roomLink.dataset.exploreRoom);
      panel?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
      panel?.focus({ preventScroll: true });
      return;
    }
    const infoLink = event.target.closest('[data-open-info]');
    if (infoLink && this.contains(infoLink)) {
      const detail = [...this.querySelectorAll('details[data-info]')].find(item => item.dataset.info === infoLink.dataset.openInfo);
      if (!detail) return;
      event.preventDefault();
      detail.open = true;
      detail.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
      detail.querySelector('summary')?.focus({ preventScroll: true });
      return;
    }
    const bookingLink = event.target.closest('[data-primary-booking]');
    if (bookingLink && this.contains(bookingLink) && this.dataset.bookingUrl) {
      const finder = this.querySelector('napton-elite-finder');
      const destination = finder?.bookingDestination?.();
      if (destination) {
        event.preventDefault();
        window.location.assign(destination);
      }
      return;
    }
    const button = event.target.closest('button');
    if (!button || !this.contains(button)) return;
    if (button.hasAttribute('data-open-photo')) {
      this.selectPhoto(Number(button.dataset.openPhoto));
      this.openViewer('gallery');
    }
    else if (button.hasAttribute('data-photo')) this.selectPhoto(Number(button.dataset.photo));
    else if (button.hasAttribute('data-open-gallery')) this.openViewer('gallery');
    else if (button.hasAttribute('data-open-plan')) this.openViewer('plan', button);
    else if (button.hasAttribute('data-viewer-close')) this.viewer?.close();
    else if (button.hasAttribute('data-viewer-next')) this.changePhoto(1);
    else if (button.hasAttribute('data-viewer-previous')) this.changePhoto(-1);
    else if (button.hasAttribute('data-viewer-zoom')) {
      const zoomed = this.viewer.classList.toggle('is-zoomed');
      button.setAttribute('aria-pressed', String(zoomed));
      button.textContent = zoomed ? button.dataset.zoomOut : button.dataset.zoomIn;
    }
  };

  handleKeydown = (event) => {
    if (!this.viewer?.open || this.mode !== 'gallery') return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      this.changePhoto(event.key === 'ArrowRight' ? 1 : -1);
    }
  };

  selectPhoto(index) {
    const photo = this.photos.find(item => Number(item.dataset.photo) === index);
    if (!photo) return;
    this.currentPhoto = this.photos.indexOf(photo);
    const image = this.querySelector('.nbp-hero-image');
    if (image instanceof HTMLImageElement) {
      image.src = photo.dataset.displaySrc;
      image.srcset = photo.dataset.srcset;
      image.alt = photo.dataset.alt;
    }
    for (const item of this.photos) item.setAttribute('aria-pressed', String(item === photo));
    const counter = this.querySelector('[data-photo-count]');
    if (counter) counter.textContent = `${this.currentPhoto + 1} / ${this.photos.length}`;
  }

  openViewer(mode, button) {
    if (!(this.viewer instanceof HTMLDialogElement)) return;
    this.viewerTrigger = button || document.activeElement;
    this.mode = mode;
    this.viewer.classList.toggle('is-plan', mode === 'plan');
    this.resetZoom();
    const controls = this.viewer.querySelector('.nbp-viewer-controls');
    controls.hidden = mode === 'plan';
    if (mode === 'plan' && button) this.setViewerImage(button.dataset.planSrc, button.dataset.planAlt);
    else this.showCurrentPhoto();
    this.viewer.showModal();
  }

  resetZoom() {
    this.viewer?.classList.remove('is-zoomed');
    const button = this.viewer?.querySelector('[data-viewer-zoom]');
    if (button) {
      button.setAttribute('aria-pressed', 'false');
      button.textContent = button.dataset.zoomIn;
    }
  }

  changePhoto(direction) {
    if (!this.photos.length || this.mode !== 'gallery') return;
    this.currentPhoto = (this.currentPhoto + direction + this.photos.length) % this.photos.length;
    this.resetZoom();
    this.showCurrentPhoto();
  }

  showCurrentPhoto() {
    const photo = this.photos[this.currentPhoto];
    if (!photo) return;
    this.setViewerImage(photo.dataset.src, photo.dataset.alt);
    const counter = this.viewer.querySelector('[data-viewer-count]');
    if (counter) counter.textContent = `${this.currentPhoto + 1} / ${this.photos.length}`;
  }

  setViewerImage(src, alt) {
    const image = this.viewer?.querySelector('.nbp-viewer-image');
    if (image instanceof HTMLImageElement && src) {
      image.src = src;
      image.alt = alt || '';
    }
    const caption = this.viewer?.querySelector('[data-viewer-caption]');
    if (caption) caption.textContent = alt || '';
    const stage = this.viewer?.querySelector('.nbp-viewer-stage');
    stage?.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }
}

if (!customElements.get('napton-boat-page')) customElements.define('napton-boat-page', NaptonBoatPage);

